import { createPortal } from 'react-dom'
import { Icon } from '../ui/Icon'
import { LETTER, TITLE, baseName, dirName, type Row } from './status'

/**
 * Source Control's LEAF renderers — one file row, one collapsible group, and the
 * portal surface the diff opens onto. Split out of GitPanel.tsx in M6 (拆面板,
 * 2026-09-03), moved verbatim; the panel keeps the state and the mutations.
 */
export function FileRow({
  row,
  selected,
  onOpen,
  onStage,
  onUnstage,
  onDiscard,
}: {
  row: Row
  selected: boolean
  onOpen: () => void
  onStage?: (() => void) | undefined
  onUnstage?: (() => void) | undefined
  onDiscard?: (() => void) | undefined
}) {
  return (
    <div className={`gitrow${selected ? ' on' : ''}`}>
      <button type="button" className="gitrow-main" onClick={onOpen} title={row.path}>
        <span className="gitrow-name">{baseName(row.path)}</span>
        <span className="gitrow-dir">{dirName(row.path)}</span>
      </button>
      <span className="gitrow-acts">
        {onDiscard && (
          <button
            type="button"
            onClick={onDiscard}
            title="Discard Changes"
            aria-label={`Discard changes in ${row.path}`}
          >
            <Icon name="undo" size={15} />
          </button>
        )}
        {onUnstage && (
          <button type="button" onClick={onUnstage} title="Unstage Changes" aria-label={`Unstage ${row.path}`}>
            <Icon name="remove" size={15} />
          </button>
        )}
        {onStage && (
          <button type="button" onClick={onStage} title="Stage Changes" aria-label={`Stage ${row.path}`}>
            <Icon name="add" size={15} />
          </button>
        )}
      </span>
      <span className={`gitrow-st st-${LETTER[row.code] ?? 'M'}`} title={TITLE[row.code] ?? row.code}>
        {LETTER[row.code] ?? row.code}
      </span>
    </div>
  )
}

/* ── collapsible group ────────────────────────────────────────────────────── */

export function Group({
  title,
  rows,
  open,
  onToggle,
  actions,
  children,
}: {
  title: string
  rows: number
  open: boolean
  onToggle: () => void
  actions?: React.ReactNode
  children: React.ReactNode
}) {
  if (rows === 0) return null
  return (
    <section className="gitgrp">
      <div className="gitgrp-hd">
        <button type="button" className="gitgrp-tog" onClick={onToggle} aria-expanded={open}>
          <Icon name={open ? 'expand_more' : 'chevron_right'} size={16} />
          <span className="gitgrp-title">{title}</span>
        </button>
        <span className="gitgrp-acts">{actions}</span>
        <span className="gitgrp-count">{rows}</span>
      </div>
      {open && <div className="gitgrp-rows">{children}</div>}
    </section>
  )
}

/**
 * Where a diff LIVES depends on where the panel sits (owner, 2026-08-27: 點選 git
 * 瀏覽器裡面的某個檔案變更…顯示應該放在 Agent session 的主視窗以蓋板的方式.
 * 寬螢幕的時候跟 File browser 分邊, 手機窄螢幕蓋著全版).
 *
 * Standalone, the panel IS a main-area surface and draws its own VS-Code split.
 * Embedded, the panel is half of a 340px drawer — a diff in there was a keyhole.
 * So the embedded diff PORTALS to the body as #gitDiffOverlay: narrow screens
 * cover everything (the drawer included); ≥900px it stops at the drawer's edge,
 * so the file browser keeps its side and the diff takes the rest of the main
 * window. The pane inside is unchanged — same header, same ✕, same queries —
 * only its stage moves.
 */
export function DiffSurface({ children }: { children: React.ReactNode }) {
  // Always portals. There was an `embedded` prop with a pass-through branch for a
  // standalone panel that nothing mounted — deleted 2026-09-02 with the rest of that mode.
  return createPortal(<div id="gitDiffOverlay">{children}</div>, document.body)
}

/* ── the panel ────────────────────────────────────────────────────────────── */
